import { useState, useEffect } from 'react';

interface UseIsReachedArgs {
  anchorRef: React.RefObject<HTMLDivElement>;
}

/**
 * Hook which returns if an element was reached in the viewport
 * @param {React.RefObject} anchorRef ref instance of an element
 * @return {boolean} isReached check if an element was reached
 */
const useIsReached = ({ anchorRef }: UseIsReachedArgs) => {
  const [isReached, setIsReached] = useState(false);

  useEffect(() => {
    const anchorRefCurrent = anchorRef.current;
    if (!anchorRefCurrent) {
      return;
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 1,
    };

    const observerCallback = ([entry]: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      if (entry.isIntersecting) {
        setIsReached(true);
        if (anchorRefCurrent) observer.unobserve(anchorRefCurrent);
      }
    };

    const observer = new IntersectionObserver(observerCallback, options);
    observer.observe(anchorRefCurrent);

    // eslint-disable-next-line consistent-return
    return () => {
      if (anchorRefCurrent) observer.unobserve(anchorRefCurrent);
    };
  }, [anchorRef]);

  return isReached;
};

export default useIsReached;
